import { z } from 'zod/v3';
import { CocoEvent } from '@cotera/client/app/etc';
import { ok } from 'neverthrow';
import { v4 } from 'uuid';
import { ResourceType } from '@cotera/api';
import type { OpenArtifactRequest } from '@cotera/client/v0/chat/artifact-panel-context';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { navigateChatSeed } from '../chat-seed';
import { agentChatSeedStrategy } from './seed';

type Params = {
  /** When set, agent will be assigned to this folder when first saved. Passed in nav state. */
  folderId?: string;
  /**
   * Whether to navigate into a fresh agent chat. Defaults to true.
   *
   * Pass `false` from a caller that is already inside an artifact panel and
   * wants the agent to land as a sibling tab beside whatever the user is
   * looking at (a workflow they're building, say) rather than navigating the
   * whole app away from it — mirroring "Add automation" on the dataset page.
   * Requires {@link openArtifact}; without it there is no panel to add a tab
   * to and this falls back to navigating.
   */
  newChat?: boolean;
  /**
   * The hosting panel's `openArtifact`, from `useArtifactPanel()`.
   *
   * Passed in rather than read here because the panel is React context whose
   * value depends on where the *caller* sits in the tree, and this action is
   * constructed once at app root with no position of its own. A caller outside
   * any panel passes nothing, which is the same as asking for a new chat.
   */
  openArtifact?: (request: OpenArtifactRequest) => void;
};

export class NewAgentAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'New Agent';
  description = 'Create a new agent';
  inputSchema = z.object({
    folderId: z.string().uuid().optional(),
    newChat: z.boolean().optional(),
    openArtifact: z
      .custom<(request: OpenArtifactRequest) => void>(
        (value) => typeof value === 'function'
      )
      .optional(),
  });
  group = 'Agent';
  icon = 'agent';

  private _lastCreatedAgentId: string | null = null;
  private _lastCreatedChatId: string | null = null;

  constructor(
    private readonly navigate: (
      path: string,
      options?: { state?: unknown }
    ) => void,
    private readonly orgSlug: string
  ) {
    super();
  }

  applicable(_context: ApplicableContext): boolean {
    return true;
  }

  async execute(
    params: Params,
    _context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const agentId = v4();

    // The agent is not persisted either way — it opens as a draft the user
    // fills in and saves. In the panel the `isNew` marker rides in the artifact
    // payload rather than a seed URL, which is what keeps the artifact the user
    // is already looking at open in the same panel.
    if (params.newChat === false && params.openArtifact !== undefined) {
      params.openArtifact({
        type: ResourceType.Agent,
        artifactId: agentId,
        payload: { agentId, isNew: true },
      });
      this._lastCreatedAgentId = agentId;
      // No chat was created, and a stale id from a previous navigate would
      // report this agent as belonging to someone else's conversation.
      this._lastCreatedChatId = null;
      return ok({ resourceId: agentId });
    }

    const { chatId } = navigateChatSeed(
      agentChatSeedStrategy,
      this.navigate,
      this.orgSlug,
      {
        artifactId: agentId,
        isNew: true,
        ...(params.folderId !== undefined && { folderId: params.folderId }),
      }
    );
    this._lastCreatedAgentId = agentId;
    this._lastCreatedChatId = chatId;
    return ok({ resourceId: agentId });
  }

  track(): ActionTrackMetadata | null {
    if (this._lastCreatedAgentId === null) {
      return null;
    }
    return {
      event: CocoEvent.AgentCreated,
      properties: {
        agentId: this._lastCreatedAgentId,
        chatId: this._lastCreatedChatId,
      },
    };
  }
}
